import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  isRejected,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  mergeServerAttachments,
  normalizeMessageAttachments,
} from "./attachments/messageAttachments";
import { pendingComposerKey } from "./attachments/pendingComposerFiles";
import {
  MessageAttachment,
  ServerMessageAttachment,
} from "./attachments/types";
import { toConversationParticipant } from "./helpers";
import { findInboxTab, InboxTabId, matchesInboxTab } from "./inboxTabs";
import {
  assignConversationToMe,
  createNewConversation,
  createNewConversationMessage,
  fetchConversationMessages,
  fetchConversations,
  fetchInboxSummary,
  fetchTeams,
  fetchTopics,
  markMessagesRead,
  searchConversations,
  transferConversation,
  updateConvStatus,
} from "./messagingActions";
import {
  conversationChangesAfterTransfer,
  memberTeamIds,
  remainsInInboxAfterTransfer,
} from "./transferConversation";
import { ParticipantOption, ParticipantType } from "./types/common";
import {
  ChatMessage,
  Conversation,
  ConversationStatus,
  ConversationTopic,
  InboxSummary,
  MessageType,
  MessagingTeam,
  Participant,
} from "./types/queries";

const conversationsAdapter = createEntityAdapter<Conversation>();
const messagesAdapter = createEntityAdapter<ChatMessage>({
  sortComparer: (a, b) => a.sentAt.localeCompare(b.sentAt),
});
const participantsAdapter = createEntityAdapter<Participant>();

type EntityMeta = {
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  nextToken?: string;
  loadingMore: boolean;
};

export interface ConversationsState
  extends EntityState<Conversation, string>,
    EntityMeta {}

export interface MessagesState
  extends EntityState<ChatMessage, string>,
    EntityMeta {
  participants: EntityState<Participant, string> &
    Omit<EntityMeta, "loadingMore">;
}

export const FLOATING_CHAT_BUTTON_BOTTOM_DEFAULT = 24;
export const FLOATING_CHAT_BUTTON_BOTTOM_RAISED = 80;

export interface ConversationSearchState {
  query: string | null;
  resultIds: string[];
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  nextToken?: string;
  loadingMore: boolean;
}

export interface MessagingState {
  conversations: ConversationsState;
  messagesByConversation: Record<string, MessagesState>;
  selectedConversationId: string | null;
  inboxTabId: InboxTabId;
  inboxSummary: InboxSummary | null;
  hasUnreadMessages: boolean;
  isFloatingWindowOpen: boolean;
  floatingChatButtonBottom: number;
  composerDrafts: Record<string, string>;
  outgoingParticipant: ParticipantOption | null;
  outgoingTitle: string;
  topicsById: Record<string, ConversationTopic>;
  topicsStatus: "idle" | "pending" | "succeeded" | "failed";
  teams: MessagingTeam[];
  teamsStatus: "idle" | "pending" | "succeeded" | "failed";
  conversationSearch: ConversationSearchState;
  // service break
  serviceUnavailable: boolean;
}

const createEmptyMessagesState = (): MessagesState => ({
  ...messagesAdapter.getInitialState(),
  participants: participantsAdapter.getInitialState({
    status: "idle",
    error: undefined,
    nextToken: undefined,
  }),
  loadingMore: false,
  status: "idle",
  error: undefined,
  nextToken: undefined,
});

const emptyConversationSearch = (): ConversationSearchState => ({
  query: null,
  resultIds: [],
  status: "idle",
  error: undefined,
  nextToken: undefined,
  loadingMore: false,
});

const clearConversationSearchState = (state: MessagingState) => {
  state.conversationSearch = emptyConversationSearch();
};

const initialState: MessagingState = {
  conversations: conversationsAdapter.getInitialState({
    status: "idle",
    error: undefined,
    nextToken: undefined,
    loadingMore: false,
  }),
  messagesByConversation: {},
  selectedConversationId: null,
  inboxTabId: "teamOpen",
  inboxSummary: null,
  hasUnreadMessages: false,
  isFloatingWindowOpen: false,
  floatingChatButtonBottom: FLOATING_CHAT_BUTTON_BOTTOM_DEFAULT,
  composerDrafts: {},
  outgoingParticipant: null,
  outgoingTitle: "",
  topicsById: {},
  topicsStatus: "idle",
  teams: [],
  teamsStatus: "idle",
  conversationSearch: emptyConversationSearch(),
  serviceUnavailable: false,
};

export const upsertConversation = createAction<Conversation>(
  "messaging/upsertConversation",
);

const messagingSlice = createSlice({
  name: "messaging",
  initialState,
  reducers: {
    changeInboxTab: (state, action: PayloadAction<InboxTabId>) => {
      state.inboxTabId = action.payload;
      clearConversationSearchState(state);
    },
    clearConversationSearch: (state) => {
      clearConversationSearchState(state);
    },
    resetMessagesByConvId: (
      state,
      action: PayloadAction<{ conversationId: string }>,
    ) => {
      if (action.payload.conversationId) {
        const { conversationId } = action.payload;
        state.messagesByConversation[conversationId] ??=
          createEmptyMessagesState();
        // idle means "not loaded". pending is reserved for an in-flight fetch.
        state.messagesByConversation[conversationId].status = "idle";
        state.messagesByConversation[conversationId].error = undefined;
      }
    },
    setSelectedConversationId: (state, action) => {
      state.selectedConversationId = action.payload;
    },
    setHasUnreadMessages: (state, action: PayloadAction<boolean>) => {
      state.hasUnreadMessages = action.payload;
    },
    setFloatingWindowOpen: (state, action: PayloadAction<boolean>) => {
      state.isFloatingWindowOpen = action.payload;
    },
    setFloatingChatButtonBottom: (state, action: PayloadAction<number>) => {
      state.floatingChatButtonBottom = action.payload;
    },
    setComposerDraft: (
      state,
      action: PayloadAction<{ conversationId: string; text: string }>,
    ) => {
      const { conversationId, text } = action.payload;
      if (!text) {
        delete state.composerDrafts[conversationId];
        return;
      }
      state.composerDrafts[conversationId] = text;
    },
    setOutgoingParticipant: (
      state,
      action: PayloadAction<ParticipantOption>,
    ) => {
      state.outgoingParticipant = action.payload;
    },
    setOutgoingTitle: (state, action: PayloadAction<string>) => {
      state.outgoingTitle = action.payload;
    },
    clearOutgoingDraft: (state) => {
      state.outgoingParticipant = null;
      state.outgoingTitle = "";
      delete state.composerDrafts[pendingComposerKey(null)];
    },
    setMessages: (
      state,
      action: PayloadAction<{ conversationId: string; message: ChatMessage }>,
    ) => {
      const { conversationId, message } = action.payload;
      const messagesState =
        state.messagesByConversation[conversationId] ??
        createEmptyMessagesState();

      // Assign the adapter result: a freshly created bucket is a plain object,
      // so addOne may return a copy instead of mutating an Immer draft.
      state.messagesByConversation[conversationId] = messagesAdapter.addOne(
        messagesState,
        message,
      );

      const conv = state.conversations.entities[conversationId];
      if (conv) {
        const isActive = state.selectedConversationId === conversationId;
        const newCount = isActive ? 0 : (conv.unreadMessageCount || 0) + 1;

        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: {
            lastMessage: {
              id: message.id,
              conversationId: message.conversationId,
              text: message.text,
              senderId: message.senderId,
              sender: message.sender,
              sentAt: message.sentAt,
            },
            unreadMessageCount: newCount,
          },
        });
      }
    },
    patchMessageAttachment: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        attachmentId: string;
        changes: Partial<MessageAttachment>;
      }>,
    ) => {
      const { conversationId, messageId, attachmentId, changes } =
        action.payload;
      const messagesState = state.messagesByConversation[conversationId];
      const message = messagesState?.entities[messageId];
      if (!messagesState || !message?.attachments) return;

      state.messagesByConversation[conversationId] = messagesAdapter.updateOne(
        messagesState,
        {
          id: messageId,
          changes: {
            attachments: message.attachments.map((attachment) =>
              attachment.id === attachmentId
                ? { ...attachment, ...changes }
                : attachment,
            ),
          },
        },
      );
    },
    applyReadReceipt: (
      state,
      action: PayloadAction<{
        conversationId: string;
        readerId: string;
        messageIds: string[];
      }>,
    ) => {
      const { conversationId, readerId, messageIds } = action.payload;
      const messagesState = state.messagesByConversation[conversationId];
      if (!messagesState || messageIds.length === 0) return;

      const updates = messageIds.flatMap((id) => {
        const message = messagesState.entities[id];
        if (!message || message.readBy?.includes(readerId)) return [];
        return [
          {
            id,
            changes: { readBy: [...(message.readBy ?? []), readerId] },
          },
        ];
      });
      if (updates.length === 0) return;

      state.messagesByConversation[conversationId] = messagesAdapter.updateMany(
        messagesState,
        updates,
      );
    },
    applyServerAttachments: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        attachments: ServerMessageAttachment[];
      }>,
    ) => {
      const { conversationId, messageId, attachments } = action.payload;
      const messagesState = state.messagesByConversation[conversationId];
      const message = messagesState?.entities[messageId];
      if (!messagesState || !message) return;

      state.messagesByConversation[conversationId] = messagesAdapter.updateOne(
        messagesState,
        {
          id: messageId,
          changes: {
            attachments: mergeServerAttachments(
              message.attachments,
              attachments,
            ),
          },
        },
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopics.pending, (state) => {
        state.topicsStatus = "pending";
      })
      .addCase(fetchTopics.fulfilled, (state, action) => {
        state.topicsById = Object.fromEntries(
          action.payload.result.items.map((topic) => [topic.id, topic]),
        );
        state.topicsStatus = "succeeded";
      })
      .addCase(fetchTopics.rejected, (state) => {
        state.topicsStatus = "failed";
      })
      .addCase(fetchInboxSummary.fulfilled, (state, action) => {
        state.inboxSummary = action.payload.result;
        state.hasUnreadMessages = action.payload.result.hasUnread;
        state.serviceUnavailable = false;
      })
      .addCase(fetchConversations.pending, (state, action) => {
        const { status, isInitial } = action.meta.arg;
        if (isInitial) {
          // initial fetch
          state.conversations.status = "pending";
          state.conversations.nextToken = undefined;
        } else {
          state.conversations.loadingMore = true;
        }

        if (!action.meta.arg.reset) {
          const tab = findInboxTab(action.meta.arg.inbox, status);
          if (tab) state.inboxTabId = tab.id;
        }
        state.conversations.error = undefined;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        const { conversations, nextToken } = action.payload.result;

        if (action.meta.arg?.reset) {
          // allow list to go empty
          conversationsAdapter.setAll(state.conversations, conversations);
        } else if (action.meta.arg?.isInitial) {
          // Replace this queue in server order. Other queues and a selected thread
          // that is not on this page stay. upsertMany would otherwise keep old ids
          // in place and append a row that was removed locally, so a transferred
          // conversation lands at the bottom until a full reload.
          const tab = findInboxTab(
            action.meta.arg.inbox,
            action.meta.arg.status,
          );
          if (tab) {
            const incomingIds = new Set(
              conversations.map((conversation) => conversation.id),
            );
            const replacedIds = state.conversations.ids.filter((id) => {
              if (id === state.selectedConversationId && !incomingIds.has(id)) {
                return false;
              }
              const conversation = state.conversations.entities[id];
              return conversation
                ? matchesInboxTab(conversation, tab.id)
                : false;
            });
            if (replacedIds.length) {
              conversationsAdapter.removeMany(state.conversations, replacedIds);
            }
          }
          conversationsAdapter.upsertMany(state.conversations, conversations);
        } else {
          // upsert for infinite scroll
          conversationsAdapter.upsertMany(state.conversations, conversations);
        }

        state.conversations.loadingMore = false;
        state.conversations.status = "succeeded";
        state.conversations.nextToken = nextToken;
      })
      .addCase(fetchConversations.rejected, (state, { error }) => {
        state.conversations.loadingMore = false;
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addCase(searchConversations.pending, (state, action) => {
        if (action.meta.arg.isInitial) {
          state.conversationSearch.status = "pending";
          state.conversationSearch.loadingMore = false;
          state.conversationSearch.query = action.meta.arg.search;
          state.conversationSearch.resultIds = [];
          state.conversationSearch.nextToken = undefined;
          state.conversationSearch.error = undefined;
          return;
        }
        state.conversationSearch.loadingMore = true;
      })
      .addCase(searchConversations.fulfilled, (state, action) => {
        if (state.conversationSearch.query !== action.meta.arg.search) {
          return;
        }
        const { conversations, nextToken } = action.payload.result;
        conversationsAdapter.upsertMany(state.conversations, conversations);
        const ids = conversations.map((conversation) => conversation.id);
        if (action.meta.arg.isInitial) {
          state.conversationSearch.resultIds = ids;
        } else {
          const existing = new Set(state.conversationSearch.resultIds);
          ids.forEach((id) => {
            if (!existing.has(id)) state.conversationSearch.resultIds.push(id);
          });
        }
        state.conversationSearch.nextToken = nextToken;
        state.conversationSearch.status = "succeeded";
        state.conversationSearch.loadingMore = false;
        state.conversationSearch.error = undefined;
      })
      .addCase(searchConversations.rejected, (state, action) => {
        if (state.conversationSearch.query !== action.meta.arg.search) {
          return;
        }
        state.conversationSearch.loadingMore = false;
        state.conversationSearch.error = action.error.message;
        if (action.meta.arg.isInitial) {
          state.conversationSearch.status = "failed";
        }
      })
      .addCase(updateConvStatus.pending, (state, action) => {
        state.conversations.status = "pending";
        state.conversations.error = undefined;
      })
      .addCase(updateConvStatus.fulfilled, (state, action) => {
        const { conversationId, status } = action.meta.arg;
        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: { status },
        });
        state.conversations.status = "succeeded";
      })
      .addCase(updateConvStatus.rejected, (state, { error }) => {
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addCase(createNewConversation.pending, (state) => {
        state.conversations.error = undefined;
      })
      .addCase(createNewConversation.fulfilled, (state, action) => {
        const { response, initiator } = action.payload;
        const { conversationId, channel } = response.result;
        const sender = { ...initiator, type: ParticipantType.EXPERT };

        const otherParticipant = action.meta.arg.otherParticipant;
        const topic = action.meta.arg.topic;
        const newConversation: Conversation = {
          id: conversationId,
          type: action.meta.arg.type,
          status: ConversationStatus.DRAFT,
          channel,
          assigneeId: null,
          initiator: sender,
          lastMessage: null,
          participants: otherParticipant
            ? [toConversationParticipant(otherParticipant)]
            : [],
          unreadMessageCount: 0,
          lastReadMessageId: null,
          createdAt: new Date().toISOString(),
          ...(topic ? { topic } : {}),
        };

        conversationsAdapter.addOne(state.conversations, newConversation);

        // move new conversation to the top of the list
        state.conversations.ids = [
          conversationId,
          ...state.conversations.ids.filter((id) => id !== conversationId),
        ];

        state.conversations.status = "succeeded";
      })
      .addCase(createNewConversation.rejected, (state, { error }) => {
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addCase(upsertConversation, (state, action) => {
        conversationsAdapter.upsertOne(state.conversations, action.payload);

        // move new conversation to the top of the list
        state.conversations.ids = [
          action.payload.id,
          ...state.conversations.ids.filter((id) => id !== action.payload.id),
        ];
      })
      .addCase(createNewConversationMessage.pending, (state, { meta }) => {
        const { conversationId, id, text, senderId, sentAt, attachments } =
          meta.arg;
        if (!conversationId || !id || !senderId || !sentAt) return;

        const messagesState = (state.messagesByConversation[conversationId] ??=
          createEmptyMessagesState());
        state.messagesByConversation[conversationId] = messagesAdapter.addOne(
          messagesState,
          {
            id,
            conversationId,
            senderId,
            text,
            messageType: MessageType.TEXT,
            sentAt,
            pending: true,
            ...(attachments?.length ? { attachments } : {}),
          },
        );
      })
      .addCase(
        createNewConversationMessage.fulfilled,
        (state, { payload, meta }) => {
          const { message, conversationId } = payload;
          state.conversations.status = "succeeded";

          const messagesState = (state.messagesByConversation[
            conversationId
          ] ??= createEmptyMessagesState());
          const optimisticId = meta.arg.id;
          const existing = optimisticId
            ? messagesState.entities[optimisticId]
            : undefined;
          if (existing && optimisticId) {
            state.messagesByConversation[conversationId] =
              messagesAdapter.updateOne(messagesState, {
                id: optimisticId,
                changes: {
                  pending: false,
                  readBy: existing.readBy ?? [],
                },
              });
            return;
          }

          state.messagesByConversation[conversationId] = messagesAdapter.addOne(
            messagesState,
            { ...message, pending: false, readBy: message.readBy ?? [] },
          );
        },
      )
      .addCase(createNewConversationMessage.rejected, (state, { meta }) => {
        const convId = meta.arg.conversationId;
        const messageId = meta.arg.id;
        const messageState = convId
          ? state.messagesByConversation[convId]
          : undefined;
        if (!messageState || !messageId) return;
        state.messagesByConversation[convId] = messagesAdapter.removeOne(
          messageState,
          messageId,
        );
      })
      .addCase(fetchConversationMessages.pending, (state, action) => {
        const { conversationId, isInitial } = action.meta.arg;

        // initialize messages state
        state.messagesByConversation[conversationId] ??=
          createEmptyMessagesState();
        const messagesState = state.messagesByConversation[conversationId];

        if (isInitial) {
          messagesState.status = "pending";
        } else {
          messagesState.loadingMore = true;
        }
        messagesState.error = undefined;
      })
      .addCase(fetchConversationMessages.fulfilled, (state, { payload }) => {
        const { conversationId, messages, participants, nextToken } =
          payload.result;

        const messagesState =
          state.messagesByConversation[conversationId] ??
          (state.messagesByConversation[conversationId] =
            createEmptyMessagesState());

        messagesAdapter.upsertMany(
          messagesState,
          messages.map((message) => ({
            ...normalizeMessageAttachments(message),
            pending: false,
          })),
        );
        participantsAdapter.upsertMany(
          messagesState.participants,
          participants,
        );

        messagesState.status = "succeeded";
        messagesState.loadingMore = false;
        messagesState.nextToken = nextToken;
      })
      .addCase(fetchConversationMessages.rejected, (state, { error, meta }) => {
        const conversationId = meta.arg.conversationId;

        const messagesState =
          state.messagesByConversation[conversationId] ??
          (state.messagesByConversation[conversationId] =
            createEmptyMessagesState());
        messagesState.status = "failed";
        messagesState.loadingMore = false;
        messagesState.error = error.message;
      })
      .addCase(fetchTeams.pending, (state) => {
        if (state.teamsStatus !== "succeeded") {
          state.teamsStatus = "pending";
        }
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.teams = action.payload;
        state.teamsStatus = "succeeded";
      })
      .addCase(fetchTeams.rejected, (state) => {
        if (state.teamsStatus !== "succeeded") {
          state.teamsStatus = "failed";
        }
      })
      .addCase(transferConversation.pending, (state) => {
        state.conversations.status = "pending";
        state.conversations.error = undefined;
      })
      .addCase(transferConversation.fulfilled, (state, { meta, payload }) => {
        const { conversationId, destination } = meta.arg;
        const existing = state.conversations.entities[conversationId];
        state.conversations.status = "succeeded";
        if (!existing) return;

        const changes = conversationChangesAfterTransfer(destination);
        const stays = remainsInInboxAfterTransfer({
          destination,
          tabId: state.inboxTabId,
          userId: payload.userId,
          initiatorId: payload.initiatorId,
          myTeamIds: memberTeamIds(state.teams, payload.userId),
        });
        if (!stays) {
          conversationsAdapter.removeOne(state.conversations, conversationId);
          if (state.selectedConversationId === conversationId) {
            state.selectedConversationId = null;
          }
          return;
        }
        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes,
        });
      })
      .addCase(transferConversation.rejected, (state, { error }) => {
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addCase(assignConversationToMe.pending, (state) => {
        state.conversations.status = "pending";
        state.conversations.error = undefined;
      })
      .addCase(assignConversationToMe.fulfilled, (state, { meta, payload }) => {
        const { conversationId } = meta.arg;
        const { participantId } = payload;

        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: {
            assigneeId: participantId,
            status: ConversationStatus.IN_PROGRESS,
          },
        });
        state.conversations.status = "succeeded";
      })
      .addCase(assignConversationToMe.rejected, (state, { error }) => {
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addCase(markMessagesRead.pending, (state) => {
        state.conversations.status = "pending";
        state.conversations.error = undefined;
      })
      .addCase(markMessagesRead.fulfilled, (state, { meta, payload }) => {
        const { conversationId } = meta.arg;

        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: {
            unreadMessageCount: 0,
          },
        });
        state.conversations.status = "succeeded";
      })
      .addCase(markMessagesRead.rejected, (state, { error }) => {
        state.conversations.status = "failed";
        state.conversations.error = error.message;
      })
      .addMatcher(
        (action) =>
          isRejected(action) &&
          action.type.startsWith("messaging/") &&
          action.error.code === "503",
        (state) => {
          state.serviceUnavailable = true;
        },
      );
  },
});

export const {
  selectAll: selectAllConversations,
  selectById: selectConversationById,
} = conversationsAdapter.getSelectors(
  (state: RootState) => state.messaging.conversations,
);

// conversation selectors
export const selectSelectedConversationId = (state: RootState) =>
  state.messaging.selectedConversationId;

export const selectIsFloatingWindowOpen = (state: RootState) =>
  state.messaging.isFloatingWindowOpen;

export const selectFloatingChatButtonBottom = (state: RootState) =>
  state.messaging.floatingChatButtonBottom;

export const selectComposerDraft = (
  state: RootState,
  conversationId?: string | null,
) =>
  conversationId ? state.messaging.composerDrafts[conversationId] ?? "" : "";

export const selectOutgoingParticipant = (state: RootState) =>
  state.messaging.outgoingParticipant;

export const selectOutgoingTitle = (state: RootState) =>
  state.messaging.outgoingTitle;

export const selectInboxTabId = (state: RootState) =>
  state.messaging.inboxTabId;

export const selectConversationsLoading = (state: RootState) =>
  state.messaging.conversations.status === "pending";

export const selectConversationsLoadingMore = (state: RootState) =>
  !!state.messaging.conversations.loadingMore;

export const selectConversationsNextToken = (state: RootState) =>
  state.messaging.conversations.nextToken;

export const selectSelectedConversation = (state: RootState) => {
  const id = selectSelectedConversationId(state);
  return id ? selectConversationById(state, id) : null;
};

export const selectSelectedChannel = (state: RootState) =>
  selectSelectedConversation(state)?.channel ?? null;

export const selectConversationsByCurrentStatus = createSelector(
  [
    (state: RootState): Conversation[] => selectAllConversations(state),
    (state: RootState): InboxTabId => state.messaging.inboxTabId,
  ],
  (all, inboxTabId) => all.filter((conv) => matchesInboxTab(conv, inboxTabId)),
);

export const selectConversationSearchQuery = (state: RootState) =>
  state.messaging.conversationSearch.query;

export const selectConversationSearchStatus = (state: RootState) =>
  state.messaging.conversationSearch.status;

export const selectConversationSearchLoading = (state: RootState) =>
  state.messaging.conversationSearch.status === "pending";

export const selectConversationSearchLoadingMore = (state: RootState) =>
  state.messaging.conversationSearch.loadingMore;

export const selectConversationSearchNextToken = (state: RootState) =>
  state.messaging.conversationSearch.nextToken;

const selectConversationSearchResultIds = (state: RootState) =>
  state.messaging.conversationSearch.resultIds;

export const selectInboxConversations = createSelector(
  [
    (state: RootState): Conversation[] => selectAllConversations(state),
    (state: RootState): InboxTabId => state.messaging.inboxTabId,
    selectConversationSearchQuery,
    selectConversationSearchResultIds,
  ],
  (all, inboxTabId, searchQuery, resultIds) => {
    if (!searchQuery) {
      return all.filter((conv) => matchesInboxTab(conv, inboxTabId));
    }
    const byId = new Map(
      all.map((conversation) => [conversation.id, conversation]),
    );
    return resultIds.flatMap((id) => {
      const conversation = byId.get(id);
      return conversation ? [conversation] : [];
    });
  },
);

// message and participant selectors
const selectMessagesByConv = (state: RootState) =>
  state.messaging.messagesByConversation;
const selectConvId = (_state: RootState, convId?: string | null) => convId;

export const selectThreadParticipants = createSelector(
  [
    selectMessagesByConv,
    (state: RootState) => state.messaging.conversations.entities,
    selectConvId,
  ],
  (messagesByConv, conversations, convId): Participant[] => {
    if (!convId) return [];
    const threadParticipants = messagesByConv[convId]?.participants;
    const fromThread = threadParticipants
      ? participantsAdapter.getSelectors().selectAll(threadParticipants)
      : [];
    if (fromThread.length > 0) return fromThread;
    return conversations[convId]?.participants ?? [];
  },
);

export const selectAllMessages = createSelector(
  [selectMessagesByConv, selectConvId],
  (messagesByConv, convId) => {
    if (!convId) return [];
    const convState: MessagesState | undefined = messagesByConv[convId];
    return convState ? messagesAdapter.getSelectors().selectAll(convState) : [];
  },
);

export const selectMessagesLoading = createSelector(
  [selectMessagesByConv, selectConvId],
  (messagesByConv, convId) =>
    convId &&
    messagesByConv[convId] &&
    messagesByConv[convId].status === "pending",
);

export const selectMessagesLoadingMore = createSelector(
  [selectMessagesByConv, selectConvId],
  (messagesByConv, convId) =>
    convId && messagesByConv[convId] && messagesByConv[convId].loadingMore,
);

export const selectMessagesNextToken = createSelector(
  [selectMessagesByConv, selectConvId],
  (messagesByConv, convId) =>
    convId ? messagesByConv[convId]?.nextToken : undefined,
);

// helper selector to get other participant
export const selectOtherParticipant = createSelector(
  [
    (s: RootState) => s.messaging.conversations,
    (_: RootState, conversationId: string | null) => conversationId,
    (_: RootState, _convId: string | null, userId?: string) => userId,
  ],
  (conversations, convId, userId): Participant | null => {
    if (!convId || !userId) return null;
    const conv = conversations.entities[convId];
    if (!conv) return null;
    return (
      conv.participants.find(
        (p) => p.id !== userId && p.type !== ParticipantType.EXPERT,
      ) || null
    );
  },
);

export const selectOtherParticipantOption = createSelector(
  [selectOtherParticipant],
  (otherParticipant) =>
    otherParticipant
      ? {
          id: otherParticipant.id,
          name: `${otherParticipant.firstName} ${otherParticipant.lastName}`,
          type: otherParticipant.type,
          city: otherParticipant?.city || "",
          customerCompanyName: otherParticipant?.customerCompanyName || "",
          talentStatus: otherParticipant?.talentStatus,
        }
      : null,
);

export const selectTeams = (state: RootState) => state.messaging.teams;

export const selectTeamsStatus = (state: RootState) =>
  state.messaging.teamsStatus;

export const selectMyTeamIds = createSelector(
  [selectTeams, (state: RootState) => state.user.user?.id],
  (teams, userId) => memberTeamIds(teams, userId),
);

export const selectTopicsById = (state: RootState) =>
  state.messaging.topicsById;

export const selectTopicsStatus = (state: RootState) =>
  state.messaging.topicsStatus;

export const selectInboxSummary = (state: RootState) =>
  state.messaging.inboxSummary;

export const selectHasUnreadMessages = (state: RootState) =>
  state.messaging.hasUnreadMessages;

export const selectMessagingServiceUnavailable = (state: RootState) =>
  state.messaging.serviceUnavailable;

export const {
  setMessages,
  setSelectedConversationId,
  setHasUnreadMessages,
  setFloatingWindowOpen,
  setFloatingChatButtonBottom,
  setComposerDraft,
  setOutgoingParticipant,
  setOutgoingTitle,
  clearOutgoingDraft,
  resetMessagesByConvId,
  changeInboxTab,
  clearConversationSearch,
  patchMessageAttachment,
  applyServerAttachments,
  applyReadReceipt,
} = messagingSlice.actions;
export const messagingReducer = messagingSlice.reducer;
